import { computed, shallowRef } from "vue";
import { defineStore } from "pinia";
import type { EditorTextPort } from "../editor/port";
import { assertNever } from "../lib/assertNever";
import { isJust, isNothing, nothing, toMaybe, type Maybe } from "../lib/maybe";
import { lookupWord } from "../lib/wordLookup";
import {
  initialWordLookupModel,
  updateWordLookup,
  type WordLookupCmd,
  type WordLookupMsg,
  type WordLookupSeed,
  type WordReplaceRange,
} from "./useWordLookupStore.update";

export type { WordLookupSeed, WordReplaceRange };

/**
 * Thesaurus look-up state. Look-up is explicit only (menu, shortcut, or
 * search submit). Never follows the caret live.
 *
 * TEA: pure Model/Msg in `useWordLookupStore.update.ts`. Lookup I/O and editor
 * replace stay in this shell; text replacement goes through the editor's
 * `EditorTextPort` (#239), registered by the editor island.
 */
export const useWordLookupStore = defineStore("wordLookup", () => {
  const model = shallowRef(initialWordLookupModel);
  let editor: Maybe<EditorTextPort> = nothing;

  async function runCmd(cmd: WordLookupCmd): Promise<void> {
    switch (cmd.kind) {
      case "lookup": {
        try {
          const result = await lookupWord(cmd.query);
          applyMsg({
            type: "LookupSucceeded",
            generation: cmd.generation,
            senses: result.senses,
            resolvedLemma: toMaybe(result.resolvedLemma),
          });
        } catch (err) {
          console.error("Word look-up failed", err);
          applyMsg({
            type: "LookupFailed",
            generation: cmd.generation,
          });
        }
        return;
      }
      default:
        assertNever(cmd as never);
    }
  }

  function applyMsg(msg: WordLookupMsg): readonly WordLookupCmd[] {
    const [next, cmds] = updateWordLookup(model.value, msg);
    model.value = next;
    return cmds;
  }

  function dispatch(msg: WordLookupMsg): void {
    const cmds = applyMsg(msg);
    for (const cmd of cmds) {
      void runCmd(cmd);
    }
  }

  async function dispatchAwait(msg: WordLookupMsg): Promise<void> {
    const cmds = applyMsg(msg);
    await Promise.all(cmds.map((cmd) => runCmd(cmd)));
  }

  const query = computed({
    get: (): string => model.value.query,
    set: (value: string): void => {
      // Keep free-typing in the search field model-local without a look-up.
      model.value = { ...model.value, query: value };
    },
  });
  const senses = computed(() => model.value.senses);
  const resolvedLemma = computed(() => model.value.resolvedLemma);
  const loading = computed(() => model.value.loading);
  const error = computed(() => model.value.error);
  const lookedUp = computed(() => model.value.lookedUp);
  const replaceRange = computed(() => model.value.replaceRange);
  const focusSearchToken = computed(() => model.value.focusSearchToken);
  const chipsReplaceEditor = computed(() => isJust(model.value.replaceRange));

  function registerEditor(next: Maybe<EditorTextPort>): void {
    editor = next;
    if (isNothing(next)) dispatch({ type: "ClearReplaceRange" });
  }

  function clearReplaceRange(): void {
    dispatch({ type: "ClearReplaceRange" });
  }

  /** Seed a replace range without a look-up (tests / apply path). */
  function setReplaceRange(range: WordReplaceRange): void {
    dispatch({ type: "SetReplaceRange", range });
  }

  function onEditorDocChanged(): void {
    clearReplaceRange();
  }

  function requestSearchFocus(): void {
    dispatch({ type: "RequestSearchFocus" });
  }

  function openFromEditor(seed: Maybe<WordLookupSeed>): void {
    dispatch({ type: "OpenFromEditor", seed });
  }

  async function submitSearch(raw?: string): Promise<void> {
    const q = (raw ?? model.value.query).trim();
    await dispatchAwait({ type: "SubmitSearch", raw: q });
  }

  async function runLookup(raw: string): Promise<void> {
    const q = raw.trim();
    if (q.length === 0) {
      dispatch({ type: "LookupEmpty" });
      return;
    }
    const generation = model.value.lookupGeneration + 1;
    await dispatchAwait({ type: "LookupStarted", query: q, generation });
  }

  function applyReplacementWord(word: string): boolean {
    const range = model.value.replaceRange;
    if (isNothing(editor) || word.length === 0 || isNothing(range))
      return false;
    // Stale or refused ranges are cleared too: the seed no longer applies.
    const replaced = editor.value.replaceExpectedRange(range.value, word);
    clearReplaceRange();
    return replaced;
  }

  return {
    query,
    senses,
    resolvedLemma,
    loading,
    error,
    lookedUp,
    replaceRange,
    focusSearchToken,
    chipsReplaceEditor,
    registerEditor,
    clearReplaceRange,
    setReplaceRange,
    onEditorDocChanged,
    requestSearchFocus,
    openFromEditor,
    submitSearch,
    runLookup,
    applyReplacementWord,
  };
});
